import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import PortalHeader from '../../components/Portal/PortalHeader';
import WaButton from '../../components/wa/WaButton';
import { PageWrap, Card, QuietState } from './customerUi';
import season from '../../canon/season.json';
import './payments.css';
import {
	subscribeToBillingProfile,
	createBillingSetupSession,
	removeBillingPaymentMethod,
	formatPaymentMethodLabel,
	formatExpiry,
	formatAccountKind,
	isExpiringBeforeSeasonEnd,
	isPendingVerification,
} from '../../services/billingService';
import {
	subscribeToInvoices,
	subscribeToLedger,
	allocatePayments,
	balanceFromLedger,
	describeBalance,
	describeLedgerEntry,
	formatCents,
	formatPeriodMonth,
	formatDateOnly,
} from '../../services/billingLedgerService';

/* Customer → Payments.
 *
 * THREE SECTIONS, AND NOTHING HERE CHARGES ANYTHING.
 *
 *   1. BALANCE (slice 2). The one sentence most families need: what they owe,
 *      or that they are square. Read from `billingProfiles.balanceCents`, the
 *      server-maintained cache written in the same transaction as every ledger
 *      entry. The ledger is re-summed client-side too, purely so a mismatch is
 *      visible rather than silently trusted — see the drift note below.
 *
 *   2. PAYMENT METHOD (slice 1). Stripe Customer + SetupIntent via hosted
 *      Checkout in `mode:'setup'`. Adding a card or bank account CHARGES
 *      NOTHING. Autopay itself is still not live: a method on file is not an
 *      enrolment, and the copy says so rather than implying a family signed up
 *      for something.
 *
 *   3. INVOICES + ACTIVITY (slice 2). Replaces the old `payments` query — a
 *      collection that never held a single document, so its empty state was
 *      the only branch that ever rendered. The real source is the
 *      family-grained invoice/ledger model, and this is it.
 *
 * PER-INVOICE "PAID" IS DERIVED, NOT STORED. Payments are money against the
 * account; invoices are immutable monthly statements. `allocatePayments`
 * applies payments oldest-first as a pure function — see
 * services/billingLedgerService.js for why that beats a stored allocation.
 *
 * WHAT A FAMILY MUST NEVER BE TOLD HERE: that a charge is coming on a date we
 * have not committed to. Until the charging slice ships, this page states what
 * is owed and that payment is still handled the way it always has been. An
 * invoice appearing is not the same as money being taken, and the copy keeps
 * those apart on purpose.
 */

// The last tuition installment of the season. A card expiring on or before it
// fails mid-year — worth saying in August, while it is a 30-second fix, rather
// than in January when a charge declines. June 1 of the season-end year.
function lastInstallmentDate() {
	const end = season.seasonEnd ? new Date(`${season.seasonEnd}T00:00:00`) : null;
	const year =
		end && !Number.isNaN(end.getTime()) ? end.getFullYear() : new Date().getFullYear() + 1;
	return new Date(year, 5, 1); // June 1
}

const Payments = () => {
	const { userProfile } = useAuth();
	const familyId = userProfile?.familyId || null;
	const [searchParams, setSearchParams] = useSearchParams();

	const [invoices, setInvoices] = useState([]);
	const [ledger, setLedger] = useState([]);
	const [loading, setLoading] = useState(true);
	const [historyError, setHistoryError] = useState(false);

	const [profile, setProfile] = useState(null);
	const [profileLoading, setProfileLoading] = useState(true);
	const [profileError, setProfileError] = useState(false);
	const [busy, setBusy] = useState(false);
	const [actionError, setActionError] = useState('');

	// ?setup=success|cancelled — where Stripe's hosted form returns to. Read
	// once and cleared from the URL, so a refresh doesn't re-announce it.
	const [returned, setReturned] = useState(null);
	useEffect(() => {
		const flag = searchParams.get('setup');
		if (!flag) return;
		setReturned(flag);
		const next = new URLSearchParams(searchParams);
		next.delete('setup');
		setSearchParams(next, { replace: true });
	}, [searchParams, setSearchParams]);

	// <wa-dialog> is driven by the `open` PROPERTY from React, never a bound
	// boolean attribute — see src/components/wa/useWaBooleanProps.js.
	const confirmRef = useRef(null);
	const [confirmRemove, setConfirmRemove] = useState(false);
	useEffect(() => {
		if (confirmRef.current) confirmRef.current.open = confirmRemove;
	}, [confirmRemove]);
	// Dismissing with Escape or the X closes the dialog WITHOUT telling React,
	// which leaves confirmRemove stuck true — after which the effect above never
	// re-fires and the dialog cannot be reopened. Same listener MakeupCalendar
	// and MyLessons carry, for the same reason.
	useEffect(() => {
		const el = confirmRef.current;
		if (!el) return undefined;
		const onHide = () => setConfirmRemove(false);
		el.addEventListener('wa-after-hide', onHide);
		return () => el.removeEventListener('wa-after-hide', onHide);
	}, []);

	// Live, because the webhook writes this document a second or two after the
	// family lands back on the page — a one-shot read would tell someone who
	// just saved a payment method that they have none.
	useEffect(() => {
		if (!familyId) {
			setProfileLoading(false);
			return undefined;
		}
		setProfileLoading(true);
		return subscribeToBillingProfile(
			familyId,
			(data) => {
				setProfile(data);
				setProfileLoading(false);
			},
			() => {
				setProfileError(true);
				setProfileLoading(false);
			}
		);
	}, [familyId]);

	// Invoices and ledger, both live. Live rather than one-shot for the same
	// reason the profile is: an admin recording a cheque while the family has
	// the page open should not leave them looking at a stale balance.
	//
	// Both subscriptions must constrain familyId to match the security rule
	// exactly — rules are not filters, and a query missing the predicate is
	// DENIED rather than broadened. The service owns those queries.
	useEffect(() => {
		if (!familyId) {
			setLoading(false);
			return undefined;
		}
		setLoading(true);
		let gotInvoices = false;
		let gotLedger = false;
		const settle = () => {
			if (gotInvoices && gotLedger) setLoading(false);
		};
		const unsubInvoices = subscribeToInvoices(
			familyId,
			(rows) => {
				setInvoices(rows);
				gotInvoices = true;
				settle();
			},
			() => {
				setHistoryError(true);
				setLoading(false);
			}
		);
		const unsubLedger = subscribeToLedger(
			familyId,
			(rows) => {
				setLedger(rows);
				gotLedger = true;
				settle();
			},
			() => {
				setHistoryError(true);
				setLoading(false);
			}
		);
		return () => {
			unsubInvoices();
			unsubLedger();
		};
	}, [familyId]);

	// The server-maintained cache is what we SHOW. The client re-sum exists only
	// to notice when the two disagree — a cache nobody checks is a cache that
	// quietly rots, and this one is money. On drift we say plainly that the
	// figure needs confirming rather than picking a winner: either number could
	// be the wrong one, and a parent guessing is worse than a parent asking.
	const cachedBalance = profile?.balanceCents;
	const ledgerBalance = useMemo(() => balanceFromLedger(ledger), [ledger]);
	const balanceDrifted =
		typeof cachedBalance === 'number' && ledger.length > 0 && cachedBalance !== ledgerBalance;
	const balance = typeof cachedBalance === 'number' ? cachedBalance : ledgerBalance;
	const balanceNote = describeBalance(ledger.length === 0 ? null : balance);

	const allocated = useMemo(() => allocatePayments(invoices, ledger), [invoices, ledger]);

	// A VOIDED INVOICE IS THE STUDIO'S BOOKKEEPING, NOT THE FAMILY'S BUSINESS
	// (Anthony, 2026-08-31: "lets default to not showing it for now. feels
	// confusing"). A cancelled statement in a parent's history invites exactly
	// one question — "did I owe this, and do I still?" — that the row itself
	// cannot answer, and the reissue that replaced it sits right beside it for
	// the same month at the same amount.
	//
	// Filtered HERE rather than in allocatePayments or subscribeToInvoices,
	// both of which are shared with the ADMIN Billing page, where voided
	// invoices must stay visible — that is where corrections are made. Note
	// allocatePayments already excludes voids from the payment pool, so this
	// changes presentation only and no figure moves.
	const visibleInvoices = useMemo(() => allocated.filter((inv) => inv.status !== 'void'), [allocated]);

	const openSetupForm = useCallback(async () => {
		setActionError('');
		setBusy(true);
		try {
			const { url } = await createBillingSetupSession();
			if (!url) throw new Error('No checkout URL returned');
			window.location.assign(url);
		} catch (err) {
			console.error('[billing] could not open setup form:', err);
			setBusy(false);
			setActionError(
				"We couldn't open the secure form just now. Please try again, or contact us if it keeps happening."
			);
		}
	}, []);

	const doRemove = useCallback(async () => {
		setActionError('');
		setBusy(true);
		try {
			await removeBillingPaymentMethod();
			setConfirmRemove(false);
		} catch (err) {
			console.error('[billing] could not remove card:', err);
			setActionError("We couldn't remove that. Please try again, or contact us.");
		} finally {
			setBusy(false);
		}
	}, []);

	const method = profile?.paymentMethod || null;
	const expiring = isExpiringBeforeSeasonEnd(method, lastInstallmentDate());
	const pending = isPendingVerification(profile);
	const isBank = method?.type === 'us_bank_account';
	const secondaryLine = isBank ? formatAccountKind(method) : formatExpiry(method);

	return (
		<>
			<PortalHeader title='Payments' subtitle='Your payment method and your payment history.' />
			<PageWrap style={{ padding: '1.5rem 1rem 3rem' }}>
				{/* NO `display: block` on a wa-callout. That idiom is right for
				    wa-card, and on a callout it OVERRIDES the component's own :host
				    layout — the icon slot stops sitting beside the message and
				    stacks above it. Invisible to jsdom; found on screen. */}
				{returned === 'success' && (
					<wa-callout variant='success' style={{ marginBottom: '1rem' }}>
						<i slot='icon' className='fa-sharp fa-solid fa-circle-check'></i>
						Your payment method is saved. Nothing has been charged.
					</wa-callout>
				)}
				{returned === 'cancelled' && (
					<wa-callout variant='neutral' style={{ marginBottom: '1rem' }}>
						<i slot='icon' className='fa-sharp fa-solid fa-circle-info'></i>
						Nothing was saved — you can add a payment method whenever you're ready.
					</wa-callout>
				)}
				{actionError && (
					<wa-callout variant='danger' style={{ marginBottom: '1rem' }}>
						<i slot='icon' className='fa-sharp fa-solid fa-triangle-exclamation'></i>
						{actionError}
					</wa-callout>
				)}

				{/* ── Balance ─────────────────────────────────────────────────── */}
				{familyId && !loading && !historyError && ledger.length > 0 && (
					<Card style={{ marginBottom: '1.5rem' }}>
						<div style={{ padding: '1.1rem 1.15rem' }}>
							<div
								style={{
									fontSize: '0.82rem',
									textTransform: 'uppercase',
									letterSpacing: '0.06em',
									color: 'var(--text-muted, #7b8499)',
									marginBottom: '0.35rem',
								}}>
								Account balance
							</div>
							<div
								style={{
									fontFamily: 'var(--font-serif)',
									fontSize: '1.9rem',
									lineHeight: 1.1,
									color:
										balanceNote.tone === 'warning'
											? 'var(--text-heading, #384971)'
											: 'var(--text-heading, #384971)',
								}}>
								{formatCents(balance)}
							</div>
							<p
								style={{
									margin: '0.4rem 0 0',
									fontSize: '0.9rem',
									color: 'var(--text-muted, #7b8499)',
								}}>
								{balanceNote.text}
							</p>

							{balanceDrifted && (
								<wa-callout variant='warning' style={{ marginTop: '0.9rem' }}>
									<i slot='icon' className='fa-sharp fa-solid fa-triangle-exclamation'></i>
									This balance needs confirming — please{' '}
									<a href='/contact'>contact us</a> before making a payment
									based on it.
								</wa-callout>
							)}
						</div>
					</Card>
				)}

				{/* ── Payment method ──────────────────────────────────────────── */}
				<Card style={{ marginBottom: '1.5rem' }}>
					<div style={{ padding: '1.1rem 1.15rem' }}>
						<h2
							style={{
								fontFamily: 'var(--font-serif)',
								fontSize: '1.15rem',
								margin: '0 0 0.15rem',
								color: 'var(--text-heading, #384971)',
							}}>
							Payment method
						</h2>
						<p
							style={{
								margin: '0 0 1rem',
								fontSize: '0.88rem',
								lineHeight: 1.5,
								color: 'var(--text-muted, #7b8499)',
							}}>
							Saving a bank account or card here gets you ready for automatic
							tuition payments this fall.{' '}
							<strong>Nothing is charged when you add it.</strong> School-year
							tuition is ${season.tuition.installmentAmount} a month across{' '}
							{season.tuition.installments} payments, beginning{' '}
							{season.tuition.firstPaymentMonth} 1.
						</p>

						{!familyId && (
							<QuietState icon='fa-circle-info'>
								Your account isn't linked to a family yet, so there's nowhere to save a
								card. <a href='/contact'>Contact us</a> and we'll get you set up.
							</QuietState>
						)}

						{familyId && profileLoading && (
							<p style={{ margin: 0, color: 'var(--text-muted, #7b8499)' }}>
								Checking for a saved card…
							</p>
						)}

						{familyId && !profileLoading && profileError && (
							<QuietState icon='fa-triangle-exclamation'>
								We couldn't load your payment details just now. Please refresh, or{' '}
								<a href='/contact'>contact us</a> if it keeps happening.
							</QuietState>
						)}

						{familyId && !profileLoading && !profileError && !method && (
							<div>
								<p style={{ margin: '0 0 0.9rem' }}>
									Nothing on file yet. A bank account is the easiest option — it
									can't expire or run into card declines mid-year. Cards work
										too, and the price is the same either way.
								</p>
								<WaButton
									variant='brand'
									loading={busy}
									disabled={busy}
									onClick={openSetupForm}>
									<i slot='start' className='fa-sharp fa-solid fa-credit-card'></i>
									Add a payment method
								</WaButton>
							</div>
						)}

						{familyId && !profileLoading && !profileError && method && (
							<div>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '0.7rem',
										padding: '0.75rem 0.9rem',
										border:
											'1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))',
										borderRadius: 'var(--radius-md, 10px)',
										marginBottom: '0.9rem',
									}}>
									<i
										className={`fa-sharp fa-solid ${
											isBank ? 'fa-building-columns' : 'fa-credit-card'
										}`}
										style={{ color: 'var(--text-muted, #7b8499)' }}
										aria-hidden='true'></i>
									<div style={{ minWidth: 0 }}>
										<div style={{ fontWeight: 600 }}>
											{formatPaymentMethodLabel(method)}
										</div>
										{secondaryLine && (
											<div
												style={{
													fontSize: '0.82rem',
													color: 'var(--text-muted, #7b8499)',
												}}>
												{secondaryLine}
											</div>
										)}
									</div>
								</div>

								{/* A bank account awaiting microdeposits is NOT ready, and
								    saying so plainly is the whole reason this state exists.
								    Stripe emails the family two small amounts to confirm. */}
								{pending && (
									<wa-callout variant='warning' style={{ marginBottom: '0.9rem' }}>
										<i slot='icon' className='fa-sharp fa-solid fa-clock'></i>
										We're still verifying this account. Stripe will email you two
										small deposits to confirm — until you do, this account can't
										be used for tuition. Nothing is charged either way.
									</wa-callout>
								)}

								{expiring && !pending && (
									<wa-callout variant='warning' style={{ marginBottom: '0.9rem' }}>
										<i
											slot='icon'
											className='fa-sharp fa-solid fa-triangle-exclamation'></i>
										This card expires before the school year ends. Adding a newer
										one now avoids a missed payment mid-year.
									</wa-callout>
								)}

								<div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
									<WaButton
										appearance='outlined'
										loading={busy}
										disabled={busy}
										onClick={openSetupForm}>
										Replace
									</WaButton>
									<WaButton
										appearance='plain'
										disabled={busy}
										onClick={() => setConfirmRemove(true)}>
										Remove
									</WaButton>
								</div>
							</div>
						)}
					</div>
				</Card>

				{/* ── Invoices ────────────────────────────────────────────────── */}
				<Card style={{ marginBottom: '1.5rem' }}>
					<div style={{ padding: '1.1rem 1.15rem' }}>
						<h2
							style={{
								fontFamily: 'var(--font-serif)',
								fontSize: '1.15rem',
								margin: '0 0 0.9rem',
								color: 'var(--text-heading, #384971)',
							}}>
							Invoices
						</h2>

						{loading && (
							<p style={{ margin: 0, color: 'var(--text-muted, #7b8499)' }}>Loading…</p>
						)}

						{!loading && historyError && (
							<QuietState icon='fa-triangle-exclamation'>
								We couldn't load your billing history just now. Please refresh, or{' '}
								<a href='/contact'>contact us</a> if it keeps happening.
							</QuietState>
						)}

						{!loading && !historyError && visibleInvoices.length === 0 && (
							<QuietState icon='fa-receipt'>
								No invoices yet. School-year tuition invoices begin in{' '}
								{season.tuition.firstPaymentMonth}.
							</QuietState>
						)}

						{!loading && !historyError && visibleInvoices.length > 0 && (
							<div style={{ overflowX: 'auto' }}>
								{/* Five columns do not fit a phone — see payments.css
								    for which two drop below 480px and why. */}
								<table
									className='cust-pay-table'
									style={{ width: '100%', borderCollapse: 'collapse' }}>
									<thead>
										<tr style={{ textAlign: 'left' }}>
											<th style={{ padding: '0.5rem 0.4rem' }}>Month</th>
											<th className='cust-pay-col-optional' style={{ padding: '0.5rem 0.4rem' }}>For</th>
											<th className='cust-pay-col-optional' style={{ padding: '0.5rem 0.4rem' }}>Due</th>
											<th style={{ padding: '0.5rem 0.4rem' }}>Amount</th>
											<th style={{ padding: '0.5rem 0.4rem' }}>Status</th>
										</tr>
									</thead>
									<tbody>
										{visibleInvoices.map((inv) => (
											<tr
												key={inv.id}
												style={{
													borderTop:
														'1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))',
												}}>
												<td style={{ padding: '0.55rem 0.4rem' }}>
													{formatPeriodMonth(inv.periodMonth)}
												</td>
												<td
													className='cust-pay-col-optional'
													style={{ padding: '0.55rem 0.4rem' }}>
													{(inv.lineItems || [])
														.map((li) =>
															// A payment-plan instalment has no student; name it
															// rather than let a $350 statement read as one $240
															// tuition line (2026-09-04).
															li.kind === 'payment_plan' ? 'Payment plan' : li.studentName
														)
														.filter(Boolean)
														.join(', ') || '—'}
												</td>
												<td
													className='cust-pay-col-optional'
													style={{ padding: '0.55rem 0.4rem' }}>
													{formatDateOnly(inv.dueOn)}
												</td>
												<td style={{ padding: '0.55rem 0.4rem' }}>
													{formatCents(inv.subtotalCents, { showCredit: false })}
												</td>
												<td style={{ padding: '0.55rem 0.4rem' }}>
													<wa-badge
														variant={
															inv.paymentStatus === 'paid'
																? 'success'
																: inv.paymentStatus === 'partial'
																? 'warning'
																: inv.paymentStatus === 'void'
																? 'neutral'
																: 'warning'
														}>
														{inv.paymentStatus === 'void'
															? 'cancelled'
															: inv.paymentStatus}
													</wa-badge>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</Card>

				{/* ── Account activity (the ledger itself) ─────────────────────── */}
				{!loading && !historyError && ledger.length > 0 && (
					<Card>
						<div style={{ padding: '1.1rem 1.15rem' }}>
							<h2
								style={{
									fontFamily: 'var(--font-serif)',
									fontSize: '1.15rem',
									margin: '0 0 0.9rem',
									color: 'var(--text-heading, #384971)',
								}}>
								Account activity
							</h2>
							<div style={{ overflowX: 'auto' }}>
								<table style={{ width: '100%', borderCollapse: 'collapse' }}>
									<thead>
										<tr style={{ textAlign: 'left' }}>
											<th style={{ padding: '0.5rem 0.4rem' }}>Date</th>
											<th style={{ padding: '0.5rem 0.4rem' }}>Description</th>
											<th style={{ padding: '0.5rem 0.4rem' }}>Amount</th>
										</tr>
									</thead>
									<tbody>
										{ledger.map((entry) => (
											<tr
												key={entry.id}
												style={{
													borderTop:
														'1px solid var(--cyv-line-cool, var(--cyv-line, #e3ddd4))',
												}}>
												<td style={{ padding: '0.55rem 0.4rem' }}>
													{entry.receivedOn
														? formatDateOnly(entry.receivedOn)
														: entry.createdAt?.toDate
														? entry.createdAt.toDate().toLocaleDateString()
														: '—'}
												</td>
												<td style={{ padding: '0.55rem 0.4rem' }}>
													{describeLedgerEntry(entry)}
												</td>
												<td style={{ padding: '0.55rem 0.4rem' }}>
													{/* Sign is meaningful: a charge adds to what is
													    owed, a payment reduces it. Rendered with an
													    explicit + / − rather than a bare number,
													    because an unsigned column of amounts on a
													    billing page is genuinely ambiguous. */}
													{entry.amountCents > 0 ? '+' : '−'}
													{formatCents(Math.abs(entry.amountCents || 0), {
														showCredit: false,
													})}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
					</Card>
				)}
			</PageWrap>

			<wa-dialog ref={confirmRef} label='Remove this payment method?'>
				<p style={{ margin: 0 }}>
					{formatPaymentMethodLabel(method)} will be removed. You can add another at
					any time — nothing is charged either way.
				</p>
				<div
					slot='footer'
					style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
					<WaButton appearance='plain' disabled={busy} onClick={() => setConfirmRemove(false)}>
						Keep it
					</WaButton>
					<WaButton variant='danger' loading={busy} disabled={busy} onClick={doRemove}>
						Remove it
					</WaButton>
				</div>
			</wa-dialog>
		</>
	);
};

export default Payments;
